'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { validateZip } from '~/actions/validate-zip'
import { funnelSchema } from '~/lib/schemas/funnel-schema'
import { useFunnelStore } from '../../_hooks/use-funnel-store'
import type { FunnelStepComponent } from '../../types'
import { FunnelStep } from '../FunnelStep'

const schema = funnelSchema.pick({ zip_code: true, city: true, state_abbreviation: true })

export const ZipCode: FunnelStepComponent = ({ onSubmit }) => {
  const { zip_code = '', city = '', state_abbreviation = '' } = useFunnelStore.getState().data
  const [isLoading, setIsLoading] = useState(false)
  const { control, formState, getValues, watch, trigger, setValue, handleSubmit } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { zip_code, city, state_abbreviation },
    mode: 'onChange',
  })
  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's watch() is not memoizable; the compiler skips this component
  const zipCodeValue = watch('zip_code')

  useEffect(() => {
    if (!schema.pick({ zip_code: true }).safeParse({ zip_code: zipCodeValue }).success) {
      setValue('city', '')
      setValue('state_abbreviation', '')
      return
    }

    setIsLoading(true)
    void validateZip(zipCodeValue)
      .then((response) => {
        setValue('city', response?.city ?? '')
        setValue('state_abbreviation', response?.state ?? '')
        void trigger()
      })
      .catch(() => undefined)
      .finally(() => setIsLoading(false))
  }, [zipCodeValue, setValue, trigger])

  return (
    <FunnelStep onSubmit={handleSubmit(onSubmit)}>
      <FunnelStep.Title>Where do you live?</FunnelStep.Title>
      <div className="flex w-full max-w-120 flex-col gap-4 tablet:max-w-none tablet:flex-row tablet:items-center">
        <div className="w-full max-w-120 shrink-0">
          <FunnelStep.NumberInput
            control={control}
            name="zip_code"
            placeholder="ZIP Code"
            allowLeadingZeros
            disabled={isLoading}
            maxChars={5}
            autoFocus
          />
        </div>
        {formState.isValid && (
          <p className="text-body-bold">{`${getValues('city')}, ${getValues('state_abbreviation')}`}</p>
        )}
      </div>
      <FunnelStep.Button disabled={!formState.isValid}>Next</FunnelStep.Button>
    </FunnelStep>
  )
}

ZipCode.path = 'zip_code'
ZipCode.schema = schema
ZipCode.formActionFields = ['zip_code']
